import React from "react";
import { DataTable, Column } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Edit, Trash } from "lucide-react";
import { ServiceCategory } from "@/types/serviceCategory";
import { useDeleteServiceCategory } from "@/components/pages/services/hooks/categories.hooks";
import { useTranslation } from "react-i18next";
import { ServiceCategoryDialog } from "./ServiceCategoryDialog";

type SortOrder = "asc" | "desc" | null;

type Props = {
  data: ServiceCategory[];
  isLoading: boolean;
  sortBy: string | null;
  sortOrder: SortOrder;
  onSort: (field: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

const ServiceCategoriesTable: React.FC<Props> = ({
  data,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const { mutate: deleteCategory, isPending: isDeleting } =
    useDeleteServiceCategory();
  const { t } = useTranslation();

  const handleDelete = (id: string) => {
    if (
      window.confirm(
        t(
          "common.confirmDelete",
          "Are you sure you want to delete this category?",
        ),
      )
    ) {
      deleteCategory(id);
    }
  };

  const columns: Column<ServiceCategory>[] = [
    {
      key: "name",
      header: t("services.categories.table.name"),
      sortable: true,
      className: "text-center",
    },
    {
      key: "services",
      header: t("services.categories.table.servicesCount"),
      sortable: false, // Sorting by count might not be supported by backend yet
      className: "text-center",
      render: (_, row) => <span>{row._count?.services || 0}</span>,
    },
    {
      key: "createdAt",
      header: t("services.categories.table.createdAt"),
      sortable: true,
      className: "text-center",
      render: (value) => <span>{new Date(value).toLocaleDateString()}</span>,
    },
    {
      key: "updatedAt",
      header: t("services.categories.table.updatedAt"),
      sortable: true,
      className: "text-center",
      render: (value) => <span>{new Date(value).toLocaleDateString()}</span>,
    },
    {
      key: "actions",
      header: t("services.categories.table.actions"),
      className: "text-center",
      render: (_, row) => (
        <div className="flex items-center justify-center gap-1">
          <ServiceCategoryDialog category={row}>
            <Button
              variant="ghost"
              size="icon"
              title={t("services.categories.table.edit")}
            >
              <Edit className="w-4 h-4" />
            </Button>
          </ServiceCategoryDialog>

          <Button
            variant="ghost"
            size="icon"
            title={t("services.categories.table.delete", "Delete")}
            onClick={() => handleDelete(row.id)}
            disabled={isDeleting}
          >
            <Trash className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      loading={isLoading}
      sorting={{
        field: sortBy ?? null,
        direction: sortOrder ?? null,
        onSort,
      }}
      pagination={{
        currentPage,
        totalPages: totalPages || 1,
        onPageChange,
      }}
      withPagination={true}
      withSorting={true}
      emptyMessage={t("services.categories.empty")}
    />
  );
};

export default ServiceCategoriesTable;
